/**
 * useTemplateAssetsReady
 *
 * Retorna `true` cuando la home/landing puede mostrarse sin flicker:
 *   1. Data del admin cargada (siteSettings + portfolio si aplica)
 *   2. Banner image precargada en el browser cache (decode async)
 *   3. Fonts del display set listas (FontFaceSet)
 *
 * Sin esto, el visitor veía la página aparecer con el hero sin banner por
 * 200-600ms hasta que la image decodeaba, generando layout shift (CLS) y
 * sensación de "loading visible" después del splash. El hook unifica las
 * 3 condiciones — el splash se mantiene visible hasta que todas pasaron.
 *
 * Timeout de seguridad: 3500ms. Si alguna condición no resuelve (banner
 * 404, font self-hosted con CORS, etc.) el hook devuelve `true` igual para
 * NO bloquear la página indefinidamente. El splash es para perfeccionar el
 * primer paint, no para garantizarlo a costa de una página que nunca renderiza.
 */

import { useEffect, useRef, useState } from "react";

/** Default safety timeout — después de esto el hook retorna ready=true igualmente. */
const SAFETY_TIMEOUT_MS = 3500;

interface UseTemplateAssetsReadyParams {
  /** True mientras siteSettings / admin config esté cargando. */
  dataLoading: boolean;
  /**
   * URL de la imagen principal (banner del hero). Si está, espera al decode.
   * Si no hay banner (`null` / `""`), salta este check.
   */
  bannerImageUrl?: string | null;
  /**
   * `false` desactiva el font-ready check (ej. tests, prerender). Default `true`.
   */
  waitForFonts?: boolean;
  /**
   * Timeout máximo en ms antes de forzar `ready=true`. Default 3500.
   * No hace falta tocarlo salvo para tests determinísticos.
   */
  safetyTimeoutMs?: number;
  /**
   * Si `true` (default), verifica que el worker haya inyectado __TEMPLATE_CONFIG__
   * con CTA resuelto antes de marcar ready. Esto previene flash de BaseTemplate
   * con CTA empty/wrong durante el primer paint cuando:
   *   - Worker cold-started y no inyectó config (raro)
   *   - Visitor accedió por Railway direct (no proxy worker)
   *   - Worker fail / network issue
   * En esos casos, esperamos hasta safetyTimeout antes de mostrar BaseTemplate
   * con su fallback de CTA hidden (mejor mostrar sin CTA que un CTA wrong).
   */
  waitForCtaResolution?: boolean;
}

function ctaConfigResolved(): boolean {
  // Worker inyecta window.__TEMPLATE_CONFIG__ con ctaType pre-resuelto en el
  // HTML SSR. Si está y tiene ctaType non-empty → el visitor verá el CTA
  // correcto desde el primer paint. Si NO está (worker cold, direct Railway),
  // BaseTemplate fallback a CTA hidden — preferimos esperar el snapshot del
  // worker hasta safety timeout.
  if (typeof window === "undefined") return false;
  const cfg = (window as unknown as {
    __TEMPLATE_CONFIG__?: { ctaType?: unknown; ctaLabel?: unknown; ctaUrl?: unknown };
  }).__TEMPLATE_CONFIG__;
  if (!cfg) return false;
  // ctaType="none" es un valor válido (admin desactivó CTA explícitamente para
  // ese geo). En ese caso no hace falta label/url. Solo flageamos "no resuelto"
  // si ctaType está completamente ausente (undefined/empty).
  const t = typeof cfg.ctaType === "string" ? cfg.ctaType.trim() : "";
  return t.length > 0;
}

export function useTemplateAssetsReady({
  dataLoading,
  bannerImageUrl,
  waitForFonts = true,
  safetyTimeoutMs = SAFETY_TIMEOUT_MS,
  waitForCtaResolution = true,
}: UseTemplateAssetsReadyParams): boolean {
  const [bannerReady, setBannerReady] = useState<boolean>(!bannerImageUrl);
  const [fontsReady, setFontsReady] = useState<boolean>(
    !waitForFonts || (typeof document !== "undefined" && document.fonts?.status === "loaded"),
  );
  const [ctaReady, setCtaReady] = useState<boolean>(
    !waitForCtaResolution || ctaConfigResolved(),
  );
  const [timedOut, setTimedOut] = useState<boolean>(false);
  // Guardamos el URL del banner que estamos esperando — si cambia (admin
  // editó el banner mientras estábamos en loading) reseteamos el ready.
  const lastBannerRef = useRef<string | null | undefined>(bannerImageUrl);

  // ── Banner image preload ────────────────────────────────────────────────────
  useEffect(() => {
    if (lastBannerRef.current !== bannerImageUrl) {
      lastBannerRef.current = bannerImageUrl;
      setBannerReady(!bannerImageUrl);
    }
    if (!bannerImageUrl) return;
    if (typeof window === "undefined") {
      setBannerReady(true);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.decoding = "async";
    img.referrerPolicy = "no-referrer-when-downgrade";
    img.onload = () => {
      if (cancelled) return;
      // decode() promociona el decode async fuera del main thread; reduce
      // jank cuando React monta el template + decodea la imagen al mismo
      // tiempo. Si decode falla (no soportado en browsers viejos), aceptamos
      // el load como suficiente.
      const finalize = () => !cancelled && setBannerReady(true);
      if (typeof img.decode === "function") {
        img.decode().then(finalize, finalize);
      } else {
        finalize();
      }
    };
    img.onerror = () => {
      // 404 / CORS / network error → no bloqueamos por una imagen rota.
      // El template renderizará con fallback (banner-fallback-bg).
      if (!cancelled) setBannerReady(true);
    };
    img.src = bannerImageUrl;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
      // Sin src reset — el browser puede seguir cacheando.
    };
  }, [bannerImageUrl]);

  // ── Fonts ready ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!waitForFonts) {
      setFontsReady(true);
      return;
    }
    if (typeof document === "undefined" || !document.fonts) {
      // SSR o browser sin FontFaceSet API — asumimos ready para no bloquear.
      setFontsReady(true);
      return;
    }
    if (document.fonts.status === "loaded") {
      setFontsReady(true);
      return;
    }
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [waitForFonts]);

  // ── CTA resolution check ────────────────────────────────────────────────────
  // Si __TEMPLATE_CONFIG__ no estaba listo al mount (worker cold, navegación
  // SPA antes de KV cache, etc), polleamos cada 100ms hasta resolverse o
  // hasta safety timeout. Esto previene flash de BaseTemplate con CTA empty
  // → el visitor solo ve el template cuando el CTA está correcto para su geo.
  useEffect(() => {
    if (!waitForCtaResolution || ctaReady) return;
    let cancelled = false;
    const poll = window.setInterval(() => {
      if (cancelled) return;
      if (ctaConfigResolved()) {
        setCtaReady(true);
        window.clearInterval(poll);
      }
    }, 100);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [waitForCtaResolution, ctaReady]);

  // ── Safety timeout ──────────────────────────────────────────────────────────
  // Aún si alguna promesa nunca resuelve (banner CDN caído, font self-hosted
  // con CORS roto, __TEMPLATE_CONFIG__ ausente), mostramos la página después
  // de safetyTimeoutMs para no dejar al visitor mirando un splash infinito.
  useEffect(() => {
    const t = window.setTimeout(() => setTimedOut(true), safetyTimeoutMs);
    return () => window.clearTimeout(t);
  }, [safetyTimeoutMs]);

  // Ready cuando: data cargó + (todos los assets/CTA listos OR timeout).
  // dataLoading lo respetamos siempre — sin data no podemos renderizar el
  // template aunque los assets estén listos. CTA-aware: si el worker no
  // inyectó el CTA, mantenemos splash hasta resolverse o timeout — esto evita
  // flash de heroCTA wrong/empty al entrar a BaseTemplate.
  if (dataLoading) return false;
  return bannerReady && fontsReady && ctaReady ? true : timedOut;
}
